import { createAsyncThunk } from '@reduxjs/toolkit';
import jwtService from 'app/services/jwtService';
import { sendMediaOrThrow } from 'app/services/mediaService/mediaService';
import { AppDispatch, RootState } from 'app/store';
import { showMessage } from 'app/store/fuse/messageSlice';
import i18n from 'i18n';
import moment from 'moment-timezone';
import countriesJson from '../../../../../i18n/countries.json';
import { setLoading } from '../../automatedMarketing/ducks/marketing.slice';
import { actions as franchiseAction } from '../../franchises/ducks/franchises.slice';
import { actions as storeActions } from '../../stores/ducks/stores.slice';
import { createStore } from '../../stores/ducks/stores.thunk';
import {
  createFranchiseApi,
  createFranchiseApiKeyV1Api,
  createFranchiseApiKeyV2Api,
  createLostCustomerMarketingApi,
  generateFranchiseDailyAppsKeyApi,
  getFranchiseApiKeyV1Api,
  getFranchiseApiKeyV2Api,
  getFranchiseDailyAppsKeyApi,
  getFranchiseSmsPriceApi,
  getFranchisesApi,
  getFranchiseWithStoresApi,
  getUserFranchiseApi,
  revokeFranchiseDailyAppsKeyApi,
  updateFranchiseApi,
  updateFranchiseAverageBasketApi,
  updateLostCustomerMarketingApi,
} from '../api';
import {
  ApiKeyFeatureType,
  FranchiseDialogType,
  FranchiseFormDTO,
  FranchiseInDTO,
  FranchiseUpdateOutDTO,
} from '../type';
import { selectUserFranchise } from './franchises.selector';
import { actions } from './franchises.slice';

export const openNewFranchiseDialog = (type?: FranchiseDialogType) => (dispatch: AppDispatch) => {
  dispatch(actions.openNewFranchiseDialog(type));
};

export const closeFranchiseDialog = (type?: FranchiseDialogType) => (dispatch: AppDispatch) => {
  dispatch(actions.closeFranchiseDialog(type));
};

export const createFranchise = createAsyncThunk(
  'franchises/createFranchises',
  async (
    { franchise, storeData, image }: { franchise: FranchiseFormDTO; storeData: any; image?: File },
    { dispatch },
  ) => {
    dispatch(storeActions.setStoreDialogIsLoading(true));
    dispatch(actions.setFranchiseDialogIsLoading(true));
    const country = countriesJson.filter(c => c.codeAlpha3 === franchise.codeAlpha3)[0];

    if (!country) {
      dispatch(
        showMessage({
          message: i18n.t('errors.generic'),
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          variant: 'error',
        }),
      );
      return { data: [] };
    }

    const imageUrl = image ? await sendMediaOrThrow(image) : franchise.logo;

    const timezone = moment.tz.zonesForCountry(country.code)[0] || moment.tz.guess();

    try {
      const request: Partial<FranchiseInDTO> = {
        logo: imageUrl,
        name: franchise.name,
        description: storeData.form.description,
        codeAlpha3: country.codeAlpha3,
        senderId: franchise.senderId,
        timezone,
        userId: franchise.userId,
        currencyCode: franchise.currencyCode,
        averageBasket: franchise.averageBasket,
        isPartner: franchise.isPartner,
      };

      const newFranchise = await createFranchiseApi(request);
      dispatch(createStore({ ...storeData, form: { ...storeData.form, userId: franchise.userId } }));

      return { data: newFranchise };
    } catch (_error) {
      dispatch(
        showMessage({
          message: i18n.t('errors.generic'),
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          variant: 'error',
        }),
      );
      return { data: [] };
    } finally {
      dispatch(actions.setFranchiseDialogIsLoading(false));
    }
  },
);

export const getFranchises = () => async (dispatch: any) => {
  try {
    const franchises = await getFranchisesApi();
    dispatch(actions.getFranchisesSuccess(franchises));
  } catch (_error) {
    dispatch(actions.getFranchisesFailed());
  }
};

export const getOfficialFranchise = () => async (dispatch: any) => {
  try {
    const userId = jwtService.getUserId();
    if (!userId) {
      throw new Error('No user id');
    }
    const officialFranchises = await getFranchiseWithStoresApi(userId);
    dispatch(franchiseAction.getFranchisesSuccess(officialFranchises));
  } catch (_error) {
    dispatch(franchiseAction.getFranchisesFailed());
  }
};

const loadFranchiseSmsPrice = async (dispatch: AppDispatch, franchiseId: string) => {
  try {
    dispatch(franchiseAction.setFranchiseSmsPrice(await getFranchiseSmsPriceApi(franchiseId)));
  } catch (_error) {
    dispatch(franchiseAction.setFranchiseSmsPrice({ isAvailable: true, unitPrice: null }));
  }
};

export const getUserFranchise = () => async (dispatch: AppDispatch, getState: () => RootState) => {
  try {
    const franchise = selectUserFranchise(getState());
    dispatch(setLoading(true));
    const userId = jwtService.getUserId();
    if (!userId) {
      throw new Error('No user id');
    }
    if (!franchise.id) {
      const userFranchise = await getUserFranchiseApi();
      dispatch(franchiseAction.updateUserFranchise(userFranchise));
    }
    const franchiseId = selectUserFranchise(getState()).id;
    loadFranchiseSmsPrice(dispatch, franchiseId);
    return franchiseId;
  } catch (_error: any) {
    dispatch(
      showMessage({
        message: i18n.t('franchise-not-found'),
        autoHideDuration: 3000,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
        variant: 'error',
      }),
    );
    return null;
  } finally {
    dispatch(setLoading(false));
  }
};

export const createDelayLostClient = createAsyncThunk(
  'automatedMarketing/defineDelayLostClient',
  async (timeSinceLastVisit: string, { dispatch, getState }) => {
    dispatch(setLoading(true));
    const state = getState() as RootState;
    try {
      const userFranchise = selectUserFranchise(state);
      if (userFranchise) {
        await createLostCustomerMarketingApi(userFranchise.id, timeSinceLastVisit);
        getUserFranchise();
      }
    } catch (error) {
      dispatch(
        showMessage({
          message: i18n.t('pages.franchises.clientQualification'),
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          variant: 'error',
        }),
      );
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  },
);

export const updateDelayLostClient =
  (timeSinceLastVisit: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      dispatch(setLoading(true));
      const userFranchise = selectUserFranchise(getState());
      if (userFranchise) {
        const { data } = await updateLostCustomerMarketingApi(userFranchise.id, timeSinceLastVisit);
        dispatch(franchiseAction.updateQualificationClientFranchise(data));
      }
    } catch (error) {
      dispatch(
        showMessage({
          message: i18n.t('pages.franchises.updateQualificationFailed'),
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          variant: 'error',
        }),
      );
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  };

export const updateFranchiseAverageBasket =
  (averageBasket: number) => async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const userFranchise = selectUserFranchise(getState());
      if (userFranchise) {
        const { data } = await updateFranchiseAverageBasketApi(userFranchise, averageBasket);
        dispatch(franchiseAction.updateUserFranchise(data));
      }
    } catch (error) {
      dispatch(
        showMessage({
          message: i18n.t('pages.franchises.updateAverageBasketFailed'),
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          variant: 'error',
        }),
      );
      throw error;
    }
  };

export const updateFranchiseInfo =
  (franchise: FranchiseUpdateOutDTO, logo?: File) => async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const franchiseLogo = logo ? await sendMediaOrThrow(logo) : franchise.logo;
      const userFranchise = selectUserFranchise(getState());
      let timezone;
      if (franchise.codeAlpha3) {
        const country = countriesJson.filter(c => c.codeAlpha3 === franchise.codeAlpha3)[0];
        timezone = moment.tz.zonesForCountry(country.code)[0] || moment.tz.guess();
      }
      if (userFranchise) {
        const { data } = await updateFranchiseApi(userFranchise, {
          ...franchise,
          timezone,
          logo: franchiseLogo,
        });
        dispatch(franchiseAction.updateUserFranchise(data));
      }
    } catch (error) {
      dispatch(
        showMessage({
          message: i18n.t('pages.franchises.updateInfoFailed'),
          autoHideDuration: 3000,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
          variant: 'error',
        }),
      );
      throw error;
    }
  };

export const getFranchiseApiKeyV1Thunk = createAsyncThunk(
  'franchises/getDeprecatedApiKey',
  async (franchiseId: string, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await getFranchiseApiKeyV1Api(franchiseId);
      return data;
    } catch (error) {
      dispatch(
        showMessage({
          message: i18n.t('pages.franchises.developer.errors.fetch'),
          variant: 'error',
        }),
      );
      return rejectWithValue(error);
    }
  },
);

export const createFranchiseApiKeyV1Thunk = createAsyncThunk(
  'franchises/createDeprecatedApiKey',
  async ({ franchiseId, userId }: { franchiseId: string; userId: string }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await createFranchiseApiKeyV1Api(userId, [
        { franchiseId, permissions: [ApiKeyFeatureType.ADD_USER] },
      ]);
      dispatch(
        showMessage({
          message: i18n.t('pages.franchises.developer.successes.create'),
          variant: 'success',
          autoHideDuration: 3000,
        }),
      );
      return data;
    } catch (error) {
      dispatch(
        showMessage({
          message: i18n.t('pages.franchises.developer.errors.create'),
          variant: 'error',
        }),
      );
      return rejectWithValue(error);
    }
  },
);

export const getFranchiseApiKeyV2Thunk = createAsyncThunk(
  'franchises/getApiKey',
  async (franchiseId: string, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await getFranchiseApiKeyV2Api(franchiseId);
      return data;
    } catch (error) {
      dispatch(
        showMessage({
          message: i18n.t('pages.franchises.developer.errors.fetch'),
          variant: 'error',
        }),
      );
      return rejectWithValue(error);
    }
  },
);

export const createFranchiseApiKeyV2Thunk = createAsyncThunk(
  'franchises/createApiKey',
  async ({ franchiseId, userId }: { franchiseId: string; userId: string }, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await createFranchiseApiKeyV2Api(franchiseId, userId);
      dispatch(
        showMessage({
          message: i18n.t('pages.franchises.developer.successes.create'),
          variant: 'success',
          autoHideDuration: 3000,
        }),
      );
      return { apiKey: data };
    } catch (error) {
      dispatch(
        showMessage({
          message: i18n.t('pages.franchises.developer.errors.create'),
          variant: 'error',
        }),
      );
      return rejectWithValue(error);
    }
  },
);

const showError = (messageKey: string) => showMessage({ message: i18n.t(messageKey), variant: 'error' });

export const getFranchiseDailyAppsKeyThunk = createAsyncThunk(
  'franchises/getDailyAppsKey',
  async (franchiseId: string, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await getFranchiseDailyAppsKeyApi(franchiseId);
      return data;
    } catch (error) {
      dispatch(showError('pages.franchises.developer.errors.fetch'));
      return rejectWithValue(error);
    }
  },
);

export const generateFranchiseDailyAppsKeyThunk = createAsyncThunk(
  'franchises/generateDailyAppsKey',
  async (franchiseId: string, { dispatch, rejectWithValue }) => {
    try {
      const { data } = await generateFranchiseDailyAppsKeyApi(franchiseId);
      dispatch(
        showMessage({
          message: i18n.t('pages.franchises.developer.successes.create'),
          variant: 'success',
          autoHideDuration: 3000,
        }),
      );
      return data;
    } catch (error) {
      dispatch(showError('pages.franchises.developer.errors.create'));
      return rejectWithValue(error);
    }
  },
);

export const revokeFranchiseDailyAppsKeyThunk = createAsyncThunk(
  'franchises/revokeDailyAppsKey',
  async (franchiseId: string, { dispatch, rejectWithValue }) => {
    try {
      await revokeFranchiseDailyAppsKeyApi(franchiseId);
    } catch (error) {
      dispatch(showError('pages.franchises.developer.errors.revoke'));
      return rejectWithValue(error);
    }
  },
);
